<script setup>
/* Tela "Propostas" — v42: ligada no simulador-api (GET /propostas).
   KPIs, filtros rápidos (Digitadas/Atualizadas Hoje), busca por CPF/código/
   nome, tabela sticky e Download CSV — a UX da captura do MAZAM AI. */
import { computed, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import simuladorApi from 'dashboard/api/simuladorApi';
/* v48: o rótulo do banco vem do catálogo da Simulação e Digitação — era
   duplicado aqui e nasceu desatualizado quando a SOMA virou dois cards. */
import { labelBanco } from '../simulacaoDigitacao/bancos.js';

const dataInicial = ref('');
const dataFinal = ref('');
const busca = ref('');
const carregando = ref(false);
const baixandoCsv = ref(false);
const propostas = ref([]);
const kpis = ref(null);
const total = ref(0);
const pagina = ref(1);
const filtroRapido = ref(''); // '' | 'hoje'
const POR_PAGINA = 50;

const totalPaginas = computed(() =>
  Math.max(1, Math.ceil((total.value || 0) / POR_PAGINA))
);

const brl = v =>
  (Number(v) || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
const cpfFmt = v => {
  const d = String(v || '').replace(/\D+/g, '');
  if (d.length !== 11) return d;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};
const dataFmt = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR');
};
const codigoCurto = c => {
  const s = String(c || '');
  if (s.length <= 13) return s || '—';
  return `${s.slice(0, 8)}…${s.slice(-4)}`;
};

/* v47: copiar (codigo completo / link) com fallback sem clipboard API. */
const copiarTexto = async (texto, aviso) => {
  if (!texto) return;
  try {
    await navigator.clipboard.writeText(texto);
  } catch (e) {
    const ta = document.createElement('textarea');
    ta.value = texto;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
  useAlert(aviso);
};

/* UY3 "em análise": clicar no badge re-consulta o SignUrl no banco. */
const verificandoLinkId = ref(null);
const verificarLink = async p => {
  if (verificandoLinkId.value) return;
  verificandoLinkId.value = p.id;
  try {
    const { data } = await simuladorApi.post(`propostas/${p.id}/atualizar-link`);
    if (data?.link) {
      p.link = data.link;
      p.aguardandoLink = false;
      useAlert('Link liberado!');
    } else {
      useAlert(data?.mensagem || 'O banco ainda não liberou o link.');
    }
  } catch (e) {
    useAlert(e.response?.data?.detail || 'Falha ao consultar o link.');
  } finally {
    verificandoLinkId.value = null;
  }
};

const COLUNAS = [
  'Código', 'Produto', 'Cliente', 'CPF', 'Banco', 'Valor', 'Situação',
  'Data Situação', 'Link',
];

const paramsAtuais = () => ({
  inicio: dataInicial.value,
  fim: dataFinal.value,
  busca: busca.value.trim(),
  pagina: pagina.value,
  por_pagina: POR_PAGINA,
});

const carregar = async () => {
  carregando.value = true;
  try {
    const { data } = await simuladorApi.get('propostas', paramsAtuais());
    propostas.value = data.propostas || [];
    total.value = data.total || 0;
    kpis.value = data.kpis || null;
  } catch (e) {
    useAlert(
      `Falha ao carregar propostas: ${e.response?.data?.detail || e.message}`
    );
  } finally {
    carregando.value = false;
  }
};

const aplicar = () => {
  pagina.value = 1;
  filtroRapido.value = '';
  carregar();
};

const hojeIso = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const digitadasHoje = () => {
  const h = hojeIso();
  dataInicial.value = h;
  dataFinal.value = h;
  filtroRapido.value = 'hoje';
  pagina.value = 1;
  carregar();
};

const limparFiltro = () => {
  dataInicial.value = '';
  dataFinal.value = '';
  busca.value = '';
  filtroRapido.value = '';
  pagina.value = 1;
  carregar();
};

const irPagina = delta => {
  const nova = pagina.value + delta;
  if (nova < 1 || nova > totalPaginas.value) return;
  pagina.value = nova;
  carregar();
};

const baixarCsv = async () => {
  baixandoCsv.value = true;
  try {
    const { data } = await simuladorApi.csv('propostas.csv', {
      inicio: dataInicial.value,
      fim: dataFinal.value,
      busca: busca.value.trim(),
    });
    const url = URL.createObjectURL(
      new Blob([data], { type: 'text/csv;charset=utf-8' })
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = 'propostas.csv';
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    useAlert('Falha ao baixar o CSV');
  } finally {
    baixandoCsv.value = false;
  }
};

const KPI_CARDS = computed(() => [
  {
    key: 'total',
    label: 'Total de propostas',
    icone: 'i-lucide-list-checks',
    valor: kpis.value ? String(kpis.value.total) : '—',
    nota: 'todas as situações',
  },
  {
    key: 'valorTotal',
    label: 'Valor total liberado',
    icone: 'i-lucide-circle-dollar-sign',
    valor: kpis.value ? brl(kpis.value.valorTotal) : '—',
    nota: 'soma do valor liberado',
  },
  {
    key: 'pendente',
    label: 'Pendente',
    icone: 'i-lucide-clock-4',
    valor: kpis.value ? brl(kpis.value.pendente) : '—',
    nota: 'aguardando assinatura/pagamento',
  },
  {
    key: 'pago',
    label: 'Pago',
    icone: 'i-lucide-badge-check',
    valor: kpis.value ? brl(kpis.value.pago) : '—',
    nota: 'propostas pagas',
  },
]);

onMounted(carregar);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <div class="flex-1 px-6 py-6 overflow-y-auto">
      <div class="flex flex-col gap-6">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="flex flex-col gap-1">
            <h1 class="text-2xl font-semibold text-n-slate-12">Propostas</h1>
            <p class="text-sm text-n-slate-11">
              Lista de propostas com filtros e localização por CPF.
            </p>
          </div>
          <div class="flex items-center gap-2">
            <button type="button" class="pr-btn-secondary" @click="carregar">
              <span
                class="i-lucide-rotate-cw size-4"
                :class="carregando ? 'animate-spin' : ''"
              />
              Atualizar
            </button>
            <button
              type="button"
              class="pr-btn-verde"
              :disabled="baixandoCsv"
              @click="baixarCsv"
            >
              <span
                :class="baixandoCsv ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-download'"
                class="size-4"
              />
              Download CSV
            </button>
          </div>
        </div>

        <!-- Indicadores -->
        <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div
            v-for="k in KPI_CARDS"
            :key="k.key"
            class="px-6 py-4 border rounded-lg border-n-weak bg-n-solid-2"
          >
            <p class="flex items-center gap-1.5 text-xs tracking-wide text-n-slate-11">
              <span :class="k.icone" class="size-3.5" />
              {{ k.label }}
            </p>
            <p class="mt-1 text-2xl font-semibold text-n-slate-12 tabular-nums">
              {{ k.valor }}
            </p>
            <p class="mt-0.5 text-[11px] text-n-slate-11">{{ k.nota }}</p>
          </div>
        </div>

        <!-- Filtros rápidos -->
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-xs text-n-slate-11">Filtros rápidos:</span>
          <button
            type="button"
            class="pr-chip"
            :class="filtroRapido === 'hoje' ? 'is-on' : ''"
            @click="digitadasHoje"
          >
            <span class="i-lucide-star size-3" />
            Digitadas Hoje
          </button>
        </div>

        <!-- Filtros de data + busca -->
        <div
          class="flex flex-wrap items-end gap-3 px-6 py-4 border rounded-lg border-n-weak bg-n-solid-2"
        >
          <div class="flex flex-col gap-1.5">
            <label class="pr-label">Data inicial</label>
            <input v-model="dataInicial" type="date" class="pr-input tabular-nums" />
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="pr-label">Data final</label>
            <input v-model="dataFinal" type="date" class="pr-input tabular-nums" />
          </div>
          <div class="flex flex-col flex-1 gap-1.5 min-w-[220px]">
            <label class="pr-label">Buscar</label>
            <div class="relative">
              <span
                class="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 size-4 text-n-slate-11 i-lucide-search"
              />
              <input
                v-model="busca"
                type="text"
                placeholder="CPF, Código ou Nome"
                class="pr-input w-full pl-9"
                @keydown.enter="aplicar"
              />
            </div>
          </div>
          <button type="button" class="pr-btn-primary" @click="aplicar">
            Buscar
          </button>
          <button
            v-if="dataInicial || dataFinal || busca"
            type="button"
            class="pr-btn-ghost"
            @click="limparFiltro"
          >
            Limpar
          </button>
        </div>

        <!-- Tabela -->
        <div
          v-if="propostas.length"
          class="w-full overflow-x-auto border rounded-lg border-n-weak"
        >
          <table class="w-full text-sm border-collapse">
            <thead class="sticky top-0 z-10 bg-n-solid-3">
              <tr>
                <th v-for="c in COLUNAS" :key="c" class="pr-th">{{ c }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="p in propostas"
                :key="p.id"
                class="border-t border-n-weak hover:bg-n-alpha-2"
              >
                <td
                  class="px-3 py-2.5 font-mono text-xs text-n-slate-12 cursor-pointer hover:text-n-slate-11"
                  :title="`${p.codigo} — clique para copiar`"
                  @click="copiarTexto(p.codigo, 'Código copiado')"
                >
                  {{ codigoCurto(p.codigo) }}
                  <span class="i-lucide-copy size-3 inline-block align-middle ml-0.5 opacity-60" />
                </td>
                <td class="px-3 py-2.5">
                  <span
                    class="px-2 py-0.5 text-[10px] font-semibold uppercase rounded bg-n-alpha-2 text-n-slate-11"
                  >
                    {{ p.produto || 'clt' }}
                  </span>
                </td>
                <td class="px-3 py-2.5 text-n-slate-12">{{ p.nome || '—' }}</td>
                <td class="px-3 py-2.5 font-mono text-xs text-n-slate-12">
                  {{ cpfFmt(p.cpf) }}
                </td>
                <td class="px-3 py-2.5 font-medium text-n-slate-12">
                  {{ labelBanco(p.banco) }}
                </td>
                <td class="px-3 py-2.5 text-n-slate-12 tabular-nums">
                  {{ brl(p.valor) }}
                </td>
                <td class="px-3 py-2.5">
                  <span class="pr-situacao">{{ p.situacao || '—' }}</span>
                </td>
                <td class="px-3 py-2.5 text-n-slate-12 tabular-nums">
                  {{ dataFmt(p.situacaoEm || p.criadaEm) }}
                </td>
                <td class="px-3 py-2.5">
                  <button
                    v-if="p.link"
                    type="button"
                    class="pr-btn-verde pr-sm"
                    @click="copiarTexto(p.link, 'Link copiado')"
                  >
                    <span class="i-lucide-link size-3.5" />
                    Copiar Link
                  </button>
                  <button
                    v-else-if="p.aguardandoLink"
                    type="button"
                    class="pr-badge-ambar"
                    title="Proposta em análise — clique para verificar se o link saiu"
                    @click="verificarLink(p)"
                  >
                    <span
                      :class="verificandoLinkId === p.id
                        ? 'i-lucide-loader-2 animate-spin'
                        : 'i-lucide-clock'"
                      class="size-3.5"
                    />
                    Aguardando {{ labelBanco(p.banco) }}
                  </button>
                  <span v-else class="text-n-slate-11">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Estado vazio honesto -->
        <div v-else class="pr-vazio">
          <div
            class="flex items-center justify-center rounded-full size-12 bg-n-alpha-2 text-n-slate-11"
          >
            <span
              :class="carregando ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-inbox'"
              class="size-6"
            />
          </div>
          <div class="flex flex-col gap-1">
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ carregando ? 'Carregando…' : 'Nenhuma proposta no período' }}
            </h3>
            <p v-if="!carregando" class="text-sm text-n-slate-11">
              Ajuste o filtro de datas ou gere propostas pela Simulação e
              Digitação.
            </p>
          </div>
        </div>

        <div
          v-if="total"
          class="flex items-center justify-between text-sm text-n-slate-11"
        >
          <span class="flex items-center gap-1.5">
            <span class="i-lucide-file-text size-4" />
            {{ total.toLocaleString('pt-BR') }} proposta(s) · página
            {{ pagina }} de {{ totalPaginas }}
          </span>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="pr-btn-secondary pr-sm"
              :disabled="pagina <= 1"
              @click="irPagina(-1)"
            >
              <span class="i-lucide-chevron-left size-4" />
              Anterior
            </button>
            <button
              type="button"
              class="pr-btn-secondary pr-sm"
              :disabled="pagina >= totalPaginas"
              @click="irPagina(1)"
            >
              Próxima
              <span class="i-lucide-chevron-right size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
/* Prefixo `pr-` e sem `scoped` pelo mesmo motivo da tela de Simulação:
   evitar colisão com o resto do Chatwoot. */
.pr-label {
  @apply text-xs font-medium text-n-slate-11;
}
.pr-input {
  @apply h-9 px-3 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 outline-none focus:border-n-brand;
}
.pr-th {
  @apply px-3 py-2 text-xs font-semibold tracking-wide text-left uppercase text-n-slate-11;
}
.pr-vazio {
  @apply flex flex-col items-center justify-center gap-3 px-6 py-16 text-center border border-dashed rounded-lg border-n-weak bg-n-solid-2;
}
.pr-btn-primary {
  @apply inline-flex items-center justify-center gap-2 px-4 text-sm font-medium text-white transition-colors rounded-md h-9 whitespace-nowrap disabled:opacity-50;
  background: #1f3ead;
}
.pr-btn-primary:hover:not(:disabled) {
  background: #1b369a;
}
.pr-btn-secondary {
  @apply inline-flex items-center justify-center gap-2 px-4 text-sm font-medium transition-colors border rounded-md h-9 border-n-weak bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-2 whitespace-nowrap disabled:opacity-50;
}
.pr-btn-ghost {
  @apply inline-flex items-center justify-center px-4 text-sm font-medium transition-colors rounded-md h-9 text-n-slate-12 hover:bg-n-alpha-2;
}
.pr-btn-verde {
  @apply inline-flex items-center justify-center gap-2 px-4 text-sm font-medium text-white transition-colors rounded-md h-9 whitespace-nowrap disabled:opacity-50;
  background: #16a34a;
}
.pr-btn-verde:hover:not(:disabled) {
  background: #15803d;
}
.pr-chip {
  @apply inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium transition-colors border rounded-full border-n-weak bg-n-solid-2 text-n-slate-11 hover:text-n-slate-12;
}
.pr-chip.is-on {
  @apply text-white border-transparent;
  background: #1f3ead;
}
.pr-situacao {
  @apply inline-flex px-2 py-0.5 text-[11px] font-medium rounded;
  background: rgba(37, 99, 235, 0.18);
  color: #93b4f8;
}
.pr-sm {
  @apply h-8 px-3 text-xs;
}
.pr-badge-ambar {
  @apply inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors;
  background: rgba(217, 119, 6, 0.16);
  color: #fbbf24;
}
.pr-badge-ambar:hover {
  background: rgba(217, 119, 6, 0.28);
}
</style>
